import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TextField, Button, Box, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment,
    Alert, Snackbar, Chip, Card, CardContent, useMediaQuery, useTheme,
    FormControl, InputLabel, Select, MenuItem, Tooltip, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const userTypeLabels = {
    0: 'Fiksni',
    1: 'Plivajući',
    2: 'Trenerski',
    3: 'Vanredni',
    4: 'Nečlanski',
    5: 'Klupski',
    6: 'Hala',
    7: 'Turnir',
    8: 'Radnik',
    9: 'Moderator',
    10: 'Free',
    11: 'Deca',
};

// Boje iz legende rezervacije termina (Schedule.jsx)
const userTypeColors = {
    0: { bg: 'gray', text: 'white' },
    1: { bg: '#e6d800', text: 'black' },
    2: { bg: 'green', text: 'white' },
    3: { bg: 'red', text: 'white' },
    4: { bg: 'blue', text: 'white' },
    5: { bg: 'brown', text: 'white' },
    6: { bg: '#607d8b', text: 'white' },
    7: { bg: '#795548', text: 'white' },
    8: { bg: '#ff9800', text: 'white' },
    9: { bg: '#d32f2f', text: 'white' },
    10: { bg: '#9e9e9e', text: 'white' },
    11: { bg: 'orange', text: 'white' },
};

const getTypeChipStyle = (type) => {
    const colors = userTypeColors[type] || { bg: '#9e9e9e', text: 'white' };
    return {
        backgroundColor: colors.bg,
        color: colors.text,
        fontWeight: 'bold',
        fontSize: '0.75rem',
    };
};

const getSaldoColor = (saldo) => {
    if (saldo > 0) return '#2e7d32'; // zelena - u pretplati
    if (saldo < 0) return '#c62828'; // crvena - duguje
    return 'inherit';
};

const formatSaldo = (saldo) => {
    if (saldo > 0) return `+${saldo} RSD`;
    return `${saldo} RSD`;
};

const Members = ({ user }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingValutaId, setEditingValutaId] = useState(null);
    const [editingValutaValue, setEditingValutaValue] = useState('');
    const [confirmResetDialog, setConfirmResetDialog] = useState({ open: false, member: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Finansijsko stanje za svakog člana { userId: saldo }
    const [financialMap, setFinancialMap] = useState({});

    // Edit član dialog
    const [editDialog, setEditDialog] = useState({ open: false, member: null });
    const [editForm, setEditForm] = useState({
        fullName: '',
        username: '',
        contact: '',
        email: '',
        year: '',
        type: 0,
        jmbg: '',
    });
    const [editSaving, setEditSaving] = useState(false);

    // Pocetno stanje
    const [pocetnoStanjeLoading, setPocetnoStanjeLoading] = useState(false);
    const [confirmPocetnoStanjeDialog, setConfirmPocetnoStanjeDialog] = useState({ open: false, godina: null });

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/users/getAllUsersFromKlub/1');
            setMembers(response.data);

            // Dohvati finansijsko stanje za svakog člana
            const finMap = {};
            await Promise.all(response.data.map(async (member) => {
                try {
                    const finResponse = await axios.get(`/api/users/financialCard/${member.id}`);
                    const data = finResponse.data;
                    finMap[member.id] = data.totalRazduzenje + data.totalOtpis - data.totalZaduzenje;
                } catch {
                    finMap[member.id] = null;
                }
            }));
            setFinancialMap(finMap);
        } catch (error) {
            console.error('Error fetching members:', error);
            setSnackbar({ open: true, message: 'Greška prilikom učitavanja članova.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (member) => {
        try {
            const response = await axios.post(`/api/users/resetPassword/${member.id}?moderatorId=${user.id}`);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setConfirmResetDialog({ open: false, member: null });
        } catch (error) {
            console.error('Error resetting password:', error);
            setSnackbar({
                open: true,
                message: 'Greška prilikom resetovanja lozinke: ' + (error.response?.data?.message || error.message),
                severity: 'error'
            });
        }
    };

    // --- Valuta inline edit ---
    const handleStartEditValuta = (member) => {
        setEditingValutaId(member.id);
        setEditingValutaValue(member.valuta.toString());
    };

    const handleCancelEditValuta = () => {
        setEditingValutaId(null);
        setEditingValutaValue('');
    };

    const handleSaveValuta = async (memberId) => {
        const valuta = parseInt(editingValutaValue);
        if (isNaN(valuta) || valuta < 0) {
            setSnackbar({ open: true, message: 'Valuta mora biti pozitivan broj.', severity: 'error' });
            return;
        }

        try {
            const response = await axios.put(`/api/users/updateValuta/${memberId}?moderatorId=${user.id}&valuta=${valuta}`);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setMembers(prev => prev.map(m => m.id === memberId ? { ...m, valuta: valuta } : m));
            setEditingValutaId(null);
            setEditingValutaValue('');
        } catch (error) {
            console.error('Error updating valuta:', error);
            setSnackbar({
                open: true,
                message: 'Greška prilikom promene valute: ' + (error.response?.data?.message || error.message),
                severity: 'error'
            });
        }
    };

    // --- Edit član dialog ---
    const handleOpenEdit = (member) => {
        setEditForm({
            fullName: member.fullName || '',
            username: member.username || '',
            contact: member.contact || '',
            email: member.email || '',
            year: member.year || '',
            type: member.type ?? 0,
            jmbg: member.jmbg || '',
        });
        setEditDialog({ open: true, member });
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveEdit = async () => {
        const member = editDialog.member;
        if (!member) return;

        setEditSaving(true);
        try {
            await axios.put(`/api/users/${member.id}`, {
                id: member.id,
                klubId: member.klubId,
                fullName: editForm.fullName,
                username: editForm.username,
                contact: editForm.contact,
                email: editForm.email || null,
                year: parseInt(editForm.year) || 0,
                type: parseInt(editForm.type),
                pol: member.pol ?? 0,
                jmbg: editForm.jmbg,
            });

            setSnackbar({ open: true, message: 'Član uspešno ažuriran!', severity: 'success' });
            setEditDialog({ open: false, member: null });

            // Osveži listu
            setMembers(prev => prev.map(m =>
                m.id === member.id
                    ? {
                        ...m,
                        fullName: editForm.fullName,
                        username: editForm.username,
                        contact: editForm.contact,
                        email: editForm.email,
                        year: parseInt(editForm.year) || 0,
                        type: parseInt(editForm.type),
                        jmbg: editForm.jmbg,
                    }
                    : m
            ));
        } catch (error) {
            console.error('Error updating member:', error);
            setSnackbar({
                open: true,
                message: 'Greška prilikom ažuriranja: ' + (error.response?.data?.message || error.message),
                severity: 'error'
            });
        } finally {
            setEditSaving(false);
        }
    };

    // --- Pocetno stanje ---
    const handlePocetnoStanje = async () => {
        setPocetnoStanjeLoading(true);
        try {
            // Prvo pokusaj da kreiras (vrati gresku ako vec postoji)
            const response = await axios.post('/api/PocetnoStanje/create-for-all-users');
            setSnackbar({
                open: true,
                message: response.data.message || 'Početno stanje uspešno kreirano!',
                severity: 'success'
            });
        } catch (error) {
            // Ako vec postoji (400 - AlreadyExists), pitaj da li da pregazi
            if (error.response && error.response.status === 400) {
                const tekucaGodina = new Date().getFullYear();
                setConfirmPocetnoStanjeDialog({ open: true, godina: tekucaGodina });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Greška: ' + (error.response?.data?.message || error.message),
                    severity: 'error'
                });
            }
        } finally {
            setPocetnoStanjeLoading(false);
        }
    };

    const handleRecreatePocetnoStanje = async () => {
        const godina = confirmPocetnoStanjeDialog.godina;
        setConfirmPocetnoStanjeDialog({ open: false, godina: null });
        setPocetnoStanjeLoading(true);
        try {
            const response = await axios.post(`/api/PocetnoStanje/recreate?godina=${godina}`);
            setSnackbar({
                open: true,
                message: response.data.message || `Početno stanje za ${godina}. godinu uspešno ponovo kreirano!`,
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Greška prilikom ponovnog kreiranja: ' + (error.response?.data?.message || error.message),
                severity: 'error'
            });
        } finally {
            setPocetnoStanjeLoading(false);
        }
    };

    const handleToggleActive = async (member) => {
        const newStatus = member.isActive === false ? 'aktivirate' : 'deaktivirate';
        if (!window.confirm(`Da li ste sigurni da želite da ${newStatus} korisnika ${member.fullName}?`)) {
            return;
        }

        try {
            const response = await axios.put(`/api/users/toggleActive/${member.id}?moderatorId=${user.id}`);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setMembers(prev => prev.map(m =>
                m.id === member.id ? { ...m, isActive: response.data.isActive } : m
            ));
        } catch (error) {
            console.error('Error toggling active status:', error);
            setSnackbar({
                open: true,
                message: 'Greška: ' + (error.response?.data?.message || error.message),
                severity: 'error'
            });
        }
    };

    const filteredMembers = members.filter(m => {
        const term = searchTerm.toLowerCase();
        return m.fullName?.toLowerCase().includes(term) ||
            m.username?.toLowerCase().includes(term) ||
            m.contact?.toLowerCase().includes(term);
    });

    if (loading) {
        return (
            <Container component="main" maxWidth="lg" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    // --- Renderuje saldo ---
    const renderSaldo = (memberId) => {
        const saldo = financialMap[memberId];
        if (saldo === null || saldo === undefined) return '—';
        return (
            <Typography
                variant="body2"
                component="span"
                sx={{
                    color: getSaldoColor(saldo),
                    fontWeight: 'bold',
                }}
            >
                {formatSaldo(saldo)}
            </Typography>
        );
    };

    // --- Renderuje valuta polje ---
    const renderValutaField = (member) => {
        if (editingValutaId === member.id) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                        type="number"
                        size="small"
                        value={editingValutaValue}
                        onChange={(e) => setEditingValutaValue(e.target.value)}
                        sx={{ width: 80 }}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveValuta(member.id);
                            if (e.key === 'Escape') handleCancelEditValuta();
                        }}
                        autoFocus
                    />
                    <IconButton size="small" color="primary" onClick={() => handleSaveValuta(member.id)}>
                        <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="default" onClick={handleCancelEditValuta}>
                        <CancelIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{member.valuta} dana</span>
                <IconButton size="small" onClick={() => handleStartEditValuta(member)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    // --- Mobile kartice ---
    const renderMobileCards = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredMembers.map((member) => {
                const typeColors = userTypeColors[member.type] || { bg: '#9e9e9e', text: 'white' };
                const saldo = financialMap[member.id];
                return (
                    <Card
                        key={member.id}
                        variant="outlined"
                        sx={{
                            borderLeft: `5px solid ${typeColors.bg}`,
                            '&:hover': { boxShadow: 3 },
                        }}
                    >
                        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                            {/* Gornji red: Ime + Chip */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {member.fullName}
                                </Typography>
                                <Chip
                                    label={userTypeLabels[member.type] || member.type}
                                    size="small"
                                    sx={getTypeChipStyle(member.type)}
                                />
                            </Box>

                            {/* Detalji */}
                            <Typography variant="body2" color="textSecondary">
                                Korisničko ime: <strong>{member.username}</strong>
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Kontakt: {member.contact || '—'}
                            </Typography>

                            {/* Finansijsko stanje */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    Finansijsko stanje:
                                </Typography>
                                {renderSaldo(member.id)}
                            </Box>

                            {/* Dodatna valuta dana */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    Dodatna valuta dana:
                                </Typography>
                                {renderValutaField(member)}
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                                (dodatni dani za plaćanje nakon isteka roka)
                            </Typography>

                            {/* Status */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    Status:
                                </Typography>
                                <Chip
                                    label={member.isActive === false ? 'Neaktivan' : 'Aktivan'}
                                    size="small"
                                    sx={{
                                        backgroundColor: member.isActive === false ? '#ffebee' : '#e8f5e9',
                                        color: member.isActive === false ? '#c62828' : '#2e7d32',
                                        fontWeight: 'bold',
                                    }}
                                />
                            </Box>

                            {/* Akcije */}
                            <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    fullWidth
                                    startIcon={<PersonIcon />}
                                    onClick={() => handleOpenEdit(member)}
                                >
                                    Izmeni
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="warning"
                                    fullWidth
                                    startIcon={<LockResetIcon />}
                                    onClick={() => setConfirmResetDialog({ open: true, member })}
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant={member.isActive === false ? "contained" : "outlined"}
                                    size="small"
                                    color={member.isActive === false ? "success" : "error"}
                                    fullWidth
                                    startIcon={member.isActive === false ? <CheckCircleIcon /> : <BlockIcon />}
                                    onClick={() => handleToggleActive(member)}
                                >
                                    {member.isActive === false ? 'Aktiviraj' : 'Deaktiviraj'}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );

    // --- Desktop tabela ---
    const renderDesktopTable = () => (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#4caf50' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Ime i prezime</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Korisničko ime</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tip</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Kontakt</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Stanje</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                            <Tooltip title="Dodatni dani valute za plaćanje nakon isteka redovnog roka" arrow>
                                <span>Dodatna valuta dana</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Akcije</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredMembers.map((member) => (
                        <TableRow key={member.id} hover>
                            <TableCell>{member.fullName}</TableCell>
                            <TableCell>{member.username}</TableCell>
                            <TableCell>
                                <Chip
                                    label={userTypeLabels[member.type] || member.type}
                                    size="small"
                                    sx={getTypeChipStyle(member.type)}
                                />
                            </TableCell>
                            <TableCell>{member.contact}</TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={member.isActive === false ? 'Neaktivan' : 'Aktivan'}
                                    size="small"
                                    sx={{
                                        backgroundColor: member.isActive === false ? '#ffebee' : '#e8f5e9',
                                        color: member.isActive === false ? '#c62828' : '#2e7d32',
                                        fontWeight: 'bold',
                                    }}
                                />
                            </TableCell>
                            <TableCell align="center">
                                {renderSaldo(member.id)}
                            </TableCell>
                            <TableCell align="center">
                                {renderValutaField(member)}
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        startIcon={<PersonIcon />}
                                        onClick={() => handleOpenEdit(member)}
                                    >
                                        Izmeni
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="warning"
                                        startIcon={<LockResetIcon />}
                                        onClick={() => setConfirmResetDialog({ open: true, member })}
                                    >
                                        Reset
                                    </Button>
                                    <Tooltip title={member.isActive === false ? 'Aktiviraj korisnika' : 'Deaktiviraj korisnika'} arrow>
                                        <Button
                                            variant={member.isActive === false ? "contained" : "outlined"}
                                            size="small"
                                            color={member.isActive === false ? "success" : "error"}
                                            startIcon={member.isActive === false ? <CheckCircleIcon /> : <BlockIcon />}
                                            onClick={() => handleToggleActive(member)}
                                        >
                                            {member.isActive === false ? 'Aktiviraj' : 'Deaktiviraj'}
                                        </Button>
                                    </Tooltip>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Container component="main" maxWidth="lg" sx={{ mt: 4, mb: 4, px: isMobile ? 1 : 3 }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
                Pregled članova
            </Typography>

            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Pretraži po imenu, korisničkom imenu ili kontaktu..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Dugme za pocetno stanje - samo za moderatora */}
            {user.type === 9 && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="info"
                        startIcon={pocetnoStanjeLoading ? <CircularProgress size={20} color="inherit" /> : <AccountBalanceIcon />}
                        onClick={handlePocetnoStanje}
                        disabled={pocetnoStanjeLoading}
                    >
                        {pocetnoStanjeLoading ? 'Kreiranje...' : 'Kreiraj početno stanje'}
                    </Button>
                </Box>
            )}

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Ukupno članova: {filteredMembers.length}
            </Typography>

            {isMobile ? renderMobileCards() : renderDesktopTable()}

            {/* Dialog za edit člana */}
            <Dialog
                open={editDialog.open}
                onClose={() => setEditDialog({ open: false, member: null })}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Izmena člana</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Ime i prezime"
                            name="fullName"
                            value={editForm.fullName}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <TextField
                            label="Korisničko ime"
                            name="username"
                            value={editForm.username}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <TextField
                            label="Kontakt"
                            name="contact"
                            value={editForm.contact}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={editForm.email}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <TextField
                            label="Godina rođenja"
                            name="year"
                            type="number"
                            value={editForm.year}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <TextField
                            label="JMBG"
                            name="jmbg"
                            value={editForm.jmbg}
                            onChange={handleEditFormChange}
                            fullWidth
                            InputLabelProps={{ shrink: true, style: { color: '#000' } }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="edit-type-label" sx={{ color: '#000' }}>Tip člana</InputLabel>
                            <Select
                                labelId="edit-type-label"
                                name="type"
                                value={editForm.type}
                                onChange={handleEditFormChange}
                                label="Tip člana"
                            >
                                {Object.entries(userTypeLabels).map(([key, label]) => (
                                    <MenuItem key={key} value={parseInt(key)}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor: (userTypeColors[parseInt(key)] || { bg: '#9e9e9e' }).bg
                                            }} />
                                            {label}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog({ open: false, member: null })}>
                        Otkaži
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveEdit}
                        disabled={editSaving}
                    >
                        {editSaving ? 'Čuvanje...' : 'Sačuvaj'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog za potvrdu reset lozinke */}
            <Dialog
                open={confirmResetDialog.open}
                onClose={() => setConfirmResetDialog({ open: false, member: null })}
            >
                <DialogTitle>Potvrda reset lozinke</DialogTitle>
                <DialogContent>
                    <Typography>
                        Da li ste sigurni da želite da resetujete lozinku za korisnika{' '}
                        <strong>{confirmResetDialog.member?.fullName}</strong> ({confirmResetDialog.member?.username})?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Nova lozinka će biti: <strong>{confirmResetDialog.member?.username}123</strong>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetDialog({ open: false, member: null })}>
                        Otkaži
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => handleResetPassword(confirmResetDialog.member)}
                    >
                        Resetuj lozinku
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog za potvrdu pregaženja početnog stanja */}
            <Dialog
                open={confirmPocetnoStanjeDialog.open}
                onClose={() => setConfirmPocetnoStanjeDialog({ open: false, godina: null })}
            >
                <DialogTitle>Početno stanje već postoji</DialogTitle>
                <DialogContent>
                    <Typography>
                        Početno stanje za <strong>{confirmPocetnoStanjeDialog.godina}.</strong> godinu je već kreirano.
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        Da li želite da <strong>pregažite</strong> staro početno stanje i ponovo ga izračunate?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5 }}>
                        Ovo će obrisati stara početna stanja i kreirati nova na osnovu trenutnih podataka.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmPocetnoStanjeDialog({ open: false, godina: null })}>
                        Otkaži
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleRecreatePocetnoStanje}
                    >
                        Da, pregazi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar za poruke */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Members;
